// =====================================================================
//  Kronaly · Módulo de Formación — datos y helpers compartidos.
//
//  Mock data de cursos, volúmenes, lecciones, materiales y foro que
//  alimenta las pantallas de Formación (catálogo, mi historial, detalle
//  y crear). Es UI-only: cuando exista el backend, estos cursos se
//  sustituyen por las respuestas del API v1 manteniendo la misma forma.
// =====================================================================

export const FORMACION_CATEGORIES = [
  { id: "todos", label: "Todos" },
  { id: "obligatorio", label: "Obligatorios" },
  { id: "compliance", label: "Compliance" },
  { id: "habilidades", label: "Habilidades" },
  { id: "producto", label: "Producto" },
  { id: "idiomas", label: "Idiomas" },
];

// Paletas de acento para la miniatura del curso: gradiente de dos paradas
// + color del glifo (monograma).
export const FORMACION_ACCENTS = {
  warning: { from: "#f9b347", to: "#b45309", glyph: "#fff8e6", chip: "var(--kr-status-warning-bg)", chipFg: "var(--kr-status-warning-fg)" },
  info: { from: "#5b8def", to: "#1e40af", glyph: "#dbeafe", chip: "var(--kr-status-info-bg)", chipFg: "var(--kr-status-info-fg)" },
  pending: { from: "#a78bfa", to: "#5b21b6", glyph: "#ede9fe", chip: "var(--kr-status-pending-bg)", chipFg: "var(--kr-status-pending-fg)" },
  brand: { from: "#6fe48d", to: "#1f6a22", glyph: "#ecfdf2", chip: "var(--kr-action-soft-bg)", chipFg: "var(--kr-action-soft-fg)" },
  slate: { from: "#6c757d", to: "#212529", glyph: "#f1f3f5", chip: "var(--kr-status-neutral-bg)", chipFg: "var(--kr-status-neutral-fg)" },
  danger: { from: "#f87171", to: "#991b1b", glyph: "#fee2e2", chip: "var(--kr-status-danger-bg)", chipFg: "var(--kr-status-danger-fg)" },
};

// -----------------------------------------------------------
// Helpers de formato y cálculo
// -----------------------------------------------------------
export function fmtMinutes(min) {
  const h = Math.floor(min / 60), m = min % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export function totalLessons(course) {
  return course.volumes.reduce((a, v) => a + v.lessons.length, 0);
}
export function completedLessons(course) {
  return course.volumes.reduce((a, v) => a + v.lessons.filter((l) => l.completed).length, 0);
}
export function nextLesson(course) {
  for (const v of course.volumes) {
    for (const l of v.lessons) {
      if (!l.completed) return { volume: v, lesson: l };
    }
  }
  // Todo completo — devolvemos la última lección.
  const lv = course.volumes[course.volumes.length - 1];
  return { volume: lv, lesson: lv.lessons[lv.lessons.length - 1] };
}

// -----------------------------------------------------------
// Constructores compactos de lecciones y materiales
// -----------------------------------------------------------
function L(id, title, mins, secs, completed = false, type = "video") {
  return { id, title, duration: `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`, durationMin: mins + secs / 60, type, completed };
}
function M(name, size, ext = "pdf") {
  return { name, size, ext };
}

export const FORMACION_COURSES = [
  {
    id: "prl-2026",
    title: "Prevención de riesgos laborales 2026",
    summary: "Marco legal actualizado, ergonomía en puesto y plan de emergencias para oficina y campo.",
    instructor: "Marta Iglesias",
    instructorRole: "Responsable PRL · Acme",
    category: "Compliance",
    categoryId: "compliance",
    type: "presentation",
    mandatory: true,
    deadline: "31 Mar 2026",
    deadlineDaysLeft: 19,
    durationMin: 82,
    students: 142,
    rating: 4.6,
    enrolled: true,
    accent: "warning",
    description: "Curso obligatorio anual que cubre la normativa española de prevención de riesgos laborales aplicada al puesto de Acme. Incluye ergonomía de oficina, prevención de incendios, primeros auxilios básicos y simulacro de evacuación.",
    learn: [
      "Identificar los riesgos típicos de tu puesto y los del entorno",
      "Aplicar las medidas ergonómicas básicas en oficina y campo",
      "Reaccionar correctamente ante una emergencia o evacuación",
      "Conocer tus derechos y deberes en materia de PRL",
    ],
    requirements: ["Ninguno. Curso para toda la plantilla."],
    volumes: [
      {
        id: "v1", title: "Marco legal y conceptos básicos", lessons: [
          L("v1-l1", "Bienvenida y objetivos del curso", 4, 12, true, "presentation"),
          L("v1-l2", "Ley 31/1995 y normativa aplicable", 6, 30, true, "presentation"),
          L("v1-l3", "Derechos y obligaciones del trabajador", 5, 18, true, "presentation"),
        ],
        materials: [M("Guía rápida PRL 2026.pdf", "1.2 MB"), M("Resumen Ley 31-1995.pdf", "480 KB")],
      },
      {
        id: "v2", title: "Ergonomía y prevención en oficina", lessons: [
          L("v2-l1", "Postura, pantalla y mobiliario", 7, 8, true, "presentation"),
          L("v2-l2", "Pausas activas: rutina de 5 min", 4, 22, true, "video"),
          L("v2-l3", "Iluminación y descanso visual", 5, 40, false, "presentation"),
          L("v2-l4", "Caso práctico: tu mesa de trabajo", 6, 0, false, "presentation"),
        ],
        materials: [M("Check-list ergonomía puesto.pdf", "320 KB"), M("Rutina pausas activas.pdf", "210 KB"), M("Plantilla evaluación.xlsx", "88 KB", "xlsx")],
      },
      {
        id: "v3", title: "Emergencias y evacuación", lessons: [
          L("v3-l1", "Plan de emergencia de Acme", 8, 4, false, "presentation"),
          L("v3-l2", "Uso del extintor (vídeo demostrativo)", 5, 55, false, "video"),
          L("v3-l3", "Rutas de evacuación por planta", 3, 30, false, "presentation"),
        ],
        materials: [M("Plano evacuación sede Madrid.pdf", "640 KB"), M("Teléfonos de emergencia.pdf", "92 KB")],
      },
      {
        id: "v4", title: "Evaluación final", lessons: [
          L("v4-l1", "Resumen y puntos clave", 4, 30, false, "presentation"),
          L("v4-l2", "Examen tipo test (10 preguntas)", 12, 0, false, "presentation"),
        ],
        materials: [M("Cuestionario revisión.pdf", "180 KB")],
      },
    ],
    forum: [
      { id: "q1", author: "Pablo Ruiz", volume: "v2", title: "¿Las pausas activas cuentan como tiempo de trabajo?", body: "Veo en el vídeo que se recomienda parar 5 min cada hora. ¿Eso se ficha como descanso o sigue siendo jornada?", replies: 4, lastReply: "Hace 4 h", solved: true, when: "Hace 2 d", lastAuthor: "Marta Iglesias" },
      { id: "q2", author: "Lucía Romero", volume: "v3", title: "Falta el plano de la oficina de Barcelona", body: "En materiales solo veo el plano de Madrid. ¿Podéis subir el de BCN antes del examen?", replies: 2, lastReply: "Ayer", solved: false, when: "Hace 3 d", lastAuthor: "Marta Iglesias" },
      { id: "q3", author: "Eduardo López", volume: "v1", title: "Validez del curso si ya hice uno en 2025", body: "Hice formación PRL hace 11 meses con otro proveedor. ¿Tengo que repetir este?", replies: 6, lastReply: "Hace 6 h", solved: true, when: "Hace 5 d", lastAuthor: "Ana Castro" },
      { id: "q4", author: "Sara Núñez", volume: "v2", title: "Soporte lumbar recomendado", body: "¿Hay un modelo de cojín lumbar que recomendéis? El de mi silla no me convence.", replies: 1, lastReply: "Hace 1 d", solved: false, when: "Hace 1 d", lastAuthor: "Marta Iglesias" },
    ],
  },

  {
    id: "rgpd-2026",
    title: "Protección de datos y RGPD",
    summary: "Cómo tratar datos personales en tu día a día: clientes, empleados y proveedores.",
    instructor: "Daniel Cabrera",
    instructorRole: "DPO · Cabrera Legal",
    category: "Compliance",
    categoryId: "compliance",
    type: "video",
    mandatory: true,
    deadline: "15 Abr 2026",
    deadlineDaysLeft: 33,
    durationMin: 138,
    students: 142,
    rating: 4.4,
    enrolled: true,
    accent: "info",
    description: "Curso anual sobre el Reglamento General de Protección de Datos aplicado al trabajo en Acme. Cubre los principios del RGPD, el manejo seguro de información personal, las bases legales del tratamiento y la respuesta ante una posible brecha de seguridad.",
    learn: [
      "Reconocer qué es un dato personal y cuándo lo estás tratando",
      "Aplicar las bases legales correctas para cada tratamiento",
      "Atender solicitudes de derechos (acceso, rectificación, supresión)",
      "Detectar y reportar una brecha de seguridad",
    ],
    requirements: ["Recomendado tras PRL 2026."],
    volumes: [
      { id: "v1", title: "Principios del RGPD", lessons: [
        L("v1-l1", "Por qué existe el RGPD", 8, 24, true),
        L("v1-l2", "Los 6 principios del tratamiento", 11, 12, true),
        L("v1-l3", "Bases legales y consentimiento", 9, 47, false),
      ], materials: [M("Resumen principios RGPD.pdf", "560 KB")] },
      { id: "v2", title: "Datos personales en tu día a día", lessons: [
        L("v2-l1", "Qué es un dato personal", 6, 18, false),
        L("v2-l2", "Manejo seguro de correos y archivos", 12, 5, false),
        L("v2-l3", "Compartir información con terceros", 8, 22, false),
        L("v2-l4", "Caso práctico: gestionar una hoja Excel con clientes", 14, 8, false),
      ], materials: [M("Buenas prácticas correo.pdf", "300 KB"), M("Checklist hoja de clientes.pdf", "210 KB")] },
      { id: "v3", title: "Derechos de los interesados", lessons: [
        L("v3-l1", "Los 8 derechos del RGPD", 10, 32, false),
        L("v3-l2", "Cómo tramitar una solicitud", 9, 18, false),
        L("v3-l3", "Plantilla de respuesta", 5, 12, false),
      ], materials: [M("Plantilla respuesta derecho ARCO.docx", "44 KB", "docx")] },
      { id: "v4", title: "Brechas de seguridad", lessons: [
        L("v4-l1", "Qué es una brecha y cómo detectarla", 8, 40, false),
        L("v4-l2", "Protocolo de notificación interno", 6, 30, false),
        L("v4-l3", "Caso real: error al enviar un correo", 9, 24, false),
      ], materials: [M("Protocolo brechas Acme.pdf", "720 KB")] },
      { id: "v5", title: "Evaluación", lessons: [
        L("v5-l1", "Examen tipo test (15 preguntas)", 15, 0, false, "presentation"),
      ], materials: [] },
    ],
    forum: [
      { id: "q1", author: "Andrea Pérez", volume: "v2", title: "Puedo guardar emails de clientes en mi Drive personal?", body: "Para terminar un proyecto rápido pensé en subir un Excel a mi Drive. ¿Es problema?", replies: 5, lastReply: "Hace 2 h", solved: true, when: "Hace 1 d", lastAuthor: "Daniel Cabrera" },
      { id: "q2", author: "Mateo Aguilar", volume: "v4", title: "Qué hago si envío un email al destinatario equivocado?", body: "Me ha pasado esta mañana — mandé una nómina a otro empleado. ¿Cuál es el siguiente paso?", replies: 8, lastReply: "Hace 30 min", solved: true, when: "Hace 4 h", lastAuthor: "Daniel Cabrera" },
      { id: "q3", author: "Sara Núñez", volume: "v3", title: "Plazo para responder a una solicitud", body: "Un proveedor antiguo pide borrar sus datos. ¿Cuánto tiempo tengo desde que entra la solicitud?", replies: 3, lastReply: "Hace 1 d", solved: true, when: "Hace 3 d", lastAuthor: "Daniel Cabrera" },
    ],
  },

  {
    id: "kronaly-onboarding",
    title: "Bienvenido a Kronaly: tu primera semana",
    summary: "Tour por la herramienta de control horario, fichaje, vacaciones y solicitudes.",
    instructor: "Ana Castro",
    instructorRole: "Manager · Producto",
    category: "Producto",
    categoryId: "producto",
    type: "video",
    mandatory: true,
    deadline: "Sin fecha límite",
    deadlineDaysLeft: null,
    durationMin: 42,
    students: 142,
    rating: 4.9,
    enrolled: true,
    accent: "brand",
    description: "Recorrido práctico por Kronaly. En 40 minutos serás autónomo para fichar, pedir vacaciones, gestionar tus documentos y entender qué ve tu manager.",
    learn: [
      "Fichar entrada, pausas y salida desde web y móvil",
      "Solicitar vacaciones, permisos y bajas",
      "Subir y firmar documentos personales",
      "Entender tu calendario y festivos asignados",
    ],
    requirements: ["Tener cuenta activa en Kronaly."],
    volumes: [
      { id: "v1", title: "Lo básico: tu jornada", lessons: [
        L("v1-l1", "Fichar entrada y salida", 5, 12, true),
        L("v1-l2", "Pausas y comidas", 4, 30, true),
        L("v1-l3", "Si olvidas fichar", 3, 18, true),
      ], materials: [M("Guía fichaje móvil.pdf", "1.4 MB")] },
      { id: "v2", title: "Solicitudes y ausencias", lessons: [
        L("v2-l1", "Pedir vacaciones", 6, 4, false),
        L("v2-l2", "Permisos retribuidos", 4, 50, false),
        L("v2-l3", "Justificar una baja", 5, 20, false),
      ], materials: [M("Tabla de permisos retribuidos.pdf", "220 KB")] },
      { id: "v3", title: "Documentos y notificaciones", lessons: [
        L("v3-l1", "Tu carpeta de documentos", 5, 18, false),
        L("v3-l2", "Firmas y aprobaciones", 4, 30, false),
        L("v3-l3", "Configurar notificaciones", 3, 24, false),
      ], materials: [] },
    ],
    forum: [
      { id: "q1", author: "Eduardo López", volume: "v1", title: "El fichaje desde la oficina no detecta mi ubicación", body: "Llevo dos mañanas que me pide aceptar la ubicación de nuevo. ¿Cambió algo?", replies: 3, lastReply: "Hace 5 h", solved: true, when: "Hace 2 d", lastAuthor: "Ana Castro" },
      { id: "q2", author: "Lucía Romero", volume: "v2", title: "Puedo pedir vacaciones a medias días?", body: "Quiero medio día el viernes. No veo la opción.", replies: 2, lastReply: "Hace 1 d", solved: false, when: "Hace 1 d", lastAuthor: "Marta Iglesias" },
    ],
  },

  {
    id: "excel-rrhh",
    title: "Excel avanzado para RRHH",
    summary: "Tablas dinámicas, fórmulas de búsqueda y cuadros de mando para datos de plantilla.",
    instructor: "Roberto Salazar",
    instructorRole: "Data analyst · Acme",
    category: "Habilidades",
    categoryId: "habilidades",
    type: "video",
    mandatory: false,
    deadline: null,
    deadlineDaysLeft: null,
    durationMin: 312,
    students: 38,
    rating: 4.7,
    enrolled: true,
    accent: "pending",
    description: "De cero a confianza con Excel para trabajos de RRHH: tablas dinámicas reales sobre datos de plantilla, fórmulas de búsqueda y referencia, validaciones y mini cuadros de mando.",
    learn: [
      "Construir una tabla dinámica útil en menos de 5 minutos",
      "Dominar BUSCARV, BUSCARX, ÍNDICE+COINCIDIR y FILTRAR",
      "Diseñar un panel de absentismo limpio",
      "Limpiar datos sucios con Power Query (intro)",
    ],
    requirements: ["Nivel intermedio de Excel (fórmulas básicas)."],
    volumes: [
      { id: "v1", title: "Tablas dinámicas a fondo", lessons: [
        L("v1-l1", "Tu primera tabla en 5 minutos", 8, 12, true),
        L("v1-l2", "Agrupar, filtrar y segmentar", 11, 20, true),
        L("v1-l3", "Campos calculados y % del total", 9, 48, false),
        L("v1-l4", "Gráficos dinámicos vinculados", 7, 12, false),
      ], materials: [M("Excel-tablas-dinamicas.xlsx", "1.6 MB", "xlsx"), M("Atajos teclado tabla dinámica.pdf", "120 KB")] },
      { id: "v2", title: "Fórmulas de búsqueda", lessons: [
        L("v2-l1", "BUSCARV y sus límites", 12, 30, false),
        L("v2-l2", "BUSCARX, la moderna", 10, 12, false),
        L("v2-l3", "ÍNDICE + COINCIDIR avanzado", 14, 18, false),
        L("v2-l4", "FILTRAR y matrices dinámicas", 11, 8, false),
      ], materials: [M("Plantilla búsquedas.xlsx", "840 KB", "xlsx")] },
      { id: "v3", title: "Limpieza de datos", lessons: [
        L("v3-l1", "Funciones de texto: ESPACIOS, NOMPROPIO, LIMPIAR", 9, 22, false),
        L("v3-l2", "Quitar duplicados sin perder filas", 6, 40, false),
        L("v3-l3", "Power Query: tu primer flujo", 18, 0, false),
      ], materials: [M("Datos sucios para practicar.xlsx", "920 KB", "xlsx")] },
      { id: "v4", title: "Mini cuadro de mando de absentismo", lessons: [
        L("v4-l1", "Diseño y wireframe del panel", 7, 28, false),
        L("v4-l2", "Conectar tablas y segmentadores", 14, 12, false),
        L("v4-l3", "KPIs en tarjetas (con CONJUNTO)", 12, 8, false),
        L("v4-l4", "Caso final guiado", 22, 0, false),
      ], materials: [M("Plantilla panel absentismo.xlsx", "2.1 MB", "xlsx"), M("Imágenes y colores corporativos.zip", "640 KB", "zip")] },
    ],
    forum: [
      { id: "q1", author: "Carlos Vidal", volume: "v1", title: "Diferencia entre tabla y tabla dinámica", body: "Quiero entender la jerarquía: Tabla → Tabla dinámica → Gráfico dinámico. ¿Cuál depende de cuál?", replies: 4, lastReply: "Hace 3 h", solved: true, when: "Hace 1 d", lastAuthor: "Roberto Salazar" },
      { id: "q2", author: "Ana Castro", volume: "v2", title: "BUSCARX me devuelve #N/D con números como texto", body: "Tengo IDs de empleado que aparecen como texto en una hoja y como número en otra. ¿Truco rápido?", replies: 7, lastReply: "Hace 1 h", solved: true, when: "Hace 1 d", lastAuthor: "Roberto Salazar" },
      { id: "q3", author: "Lucía Romero", volume: "v3", title: "Power Query no aparece en mi Excel", body: "Tengo Excel 2019 LTSC. ¿Está incluido o necesito un complemento?", replies: 2, lastReply: "Hace 4 h", solved: false, when: "Hace 2 d", lastAuthor: "Roberto Salazar" },
    ],
  },

  {
    id: "liderazgo-equipos",
    title: "Liderazgo y gestión de equipos remotos",
    summary: "Construir confianza, dar feedback útil y mantener foco en equipos distribuidos.",
    instructor: "Inés Vega",
    instructorRole: "Coach ejecutiva · Externa",
    category: "Habilidades",
    categoryId: "habilidades",
    type: "video",
    mandatory: false,
    deadline: null,
    deadlineDaysLeft: null,
    durationMin: 215,
    students: 24,
    rating: 4.8,
    enrolled: false,
    accent: "pending",
    description: "Curso para managers nuevos o que quieren afilar su práctica. Foco en equipos remotos o híbridos: rituales, feedback 1:1, conflicto y cultura.",
    learn: [
      "Diseñar rituales de equipo que valgan la pena",
      "Dar feedback que cale (modelo SBI)",
      "Identificar y desactivar conflictos antes de que escalen",
      "Definir y reforzar valores de equipo",
    ],
    requirements: ["Estar liderando o por liderar un equipo de 2+ personas."],
    volumes: [
      { id: "v1", title: "Fundamentos: confianza", lessons: [
        L("v1-l1", "Los 4 ingredientes de la confianza", 12, 8, false),
        L("v1-l2", "Vulnerabilidad sin perder autoridad", 10, 30, false),
        L("v1-l3", "Caso: el manager nuevo", 14, 12, false),
      ], materials: [M("Mapa de confianza equipo.pdf", "440 KB")] },
      { id: "v2", title: "Rituales del equipo remoto", lessons: [
        L("v2-l1", "Stand-up que no aburre", 8, 18, false),
        L("v2-l2", "1:1 efectivos en 25 min", 11, 12, false),
        L("v2-l3", "Retros que generan acciones", 13, 30, false),
      ], materials: [M("Plantilla 1:1 quincenal.pdf", "180 KB"), M("Guion retro Lean Coffee.pdf", "240 KB")] },
      { id: "v3", title: "Feedback en serio", lessons: [
        L("v3-l1", "Modelo SBI: Situation-Behaviour-Impact", 12, 14, false),
        L("v3-l2", "Cuando hay que dar una conversación difícil", 18, 0, false),
        L("v3-l3", "Pedir feedback (no solo darlo)", 9, 22, false),
      ], materials: [M("Guía SBI con ejemplos.pdf", "320 KB")] },
      { id: "v4", title: "Conflicto y cultura", lessons: [
        L("v4-l1", "Tipos de conflicto productivo", 10, 8, false),
        L("v4-l2", "Mediar sin tomar partido", 13, 12, false),
        L("v4-l3", "Valores accionables", 12, 30, false),
      ], materials: [] },
    ],
    forum: [
      { id: "q1", author: "Marta Iglesias", volume: "v2", title: "1:1 cada cuánto?", body: "Tengo 7 reportes directos. Semanal me parece imposible. ¿Quincenal de 25 min funciona?", replies: 6, lastReply: "Hace 2 h", solved: true, when: "Hace 2 d", lastAuthor: "Inés Vega" },
    ],
  },

  {
    id: "ingles-b2",
    title: "Inglés de negocios B2",
    summary: "Reuniones, emails y presentaciones con confianza. 12 unidades con tutoría.",
    instructor: "Helen Whitaker",
    instructorRole: "Tutora · Cambridge Cert.",
    category: "Idiomas",
    categoryId: "idiomas",
    type: "video",
    mandatory: false,
    deadline: null,
    deadlineDaysLeft: null,
    durationMin: 1080,
    students: 18,
    rating: 4.5,
    enrolled: false,
    accent: "info",
    description: "Programa de 12 unidades para alcanzar el nivel B2 en contexto profesional. Cada unidad combina vídeo, escucha activa y un ejercicio enviado a tu tutora.",
    learn: [
      "Conducir reuniones en inglés con vocabulario adecuado",
      "Escribir emails claros, breves y educados",
      "Presentar datos y proyectos en inglés",
      "Negociar y rebatir argumentos sin perder el hilo",
    ],
    requirements: ["Nivel B1 o equivalente. Prueba de nivel disponible al apuntarte."],
    volumes: [
      { id: "v1", title: "Unit 01 · Meetings (Intro)", lessons: [
        L("v1-l1", "Opening a meeting", 12, 0, false),
        L("v1-l2", "Agreeing and disagreeing politely", 14, 0, false),
        L("v1-l3", "Wrap-up and action items", 10, 30, false),
      ], materials: [M("Unit 01 — Vocabulary.pdf", "180 KB"), M("Unit 01 — Practice.pdf", "240 KB")] },
      { id: "v2", title: "Unit 02 · Emails that work", lessons: [
        L("v2-l1", "Tone and register", 11, 18, false),
        L("v2-l2", "Templates for common situations", 18, 0, false),
        L("v2-l3", "Common mistakes", 9, 22, false),
      ], materials: [M("Unit 02 — Email templates.pdf", "260 KB")] },
      { id: "v3", title: "Unit 03 · Presenting data", lessons: [
        L("v3-l1", "Charts and trends vocabulary", 13, 12, false),
        L("v3-l2", "Walking the audience through a slide", 16, 0, false),
        L("v3-l3", "Q&A: handling tricky questions", 14, 30, false),
      ], materials: [] },
    ],
    forum: [
      { id: "q1", author: "Pablo Ruiz", volume: "v1", title: "Pronunciation drills outside class?", body: "Helen — could you recommend a tool to practise pronunciation on my own? Thanks!", replies: 2, lastReply: "Hace 1 d", solved: true, when: "Hace 4 d", lastAuthor: "Helen Whitaker" },
    ],
  },

  {
    id: "acoso-laboral",
    title: "Prevención del acoso laboral",
    summary: "Cómo identificar, reportar y prevenir situaciones de acoso en el entorno laboral.",
    instructor: "Cabrera Legal",
    instructorRole: "Equipo formador externo",
    category: "Compliance",
    categoryId: "compliance",
    type: "presentation",
    mandatory: false,
    deadline: null,
    deadlineDaysLeft: null,
    durationMin: 55,
    students: 142,
    rating: 4.5,
    enrolled: false,
    accent: "danger",
    description: "Formación clave para entender qué es el acoso laboral, cómo se identifica, qué canales tiene Acme para reportarlo y cómo prevenirlo desde el día a día.",
    learn: [
      "Diferenciar conflicto, conducta inapropiada y acoso",
      "Reconocer señales tempranas",
      "Usar el canal interno de denuncia",
      "Tu papel como compañero/a o manager",
    ],
    requirements: ["Recomendado a toda la plantilla."],
    volumes: [
      { id: "v1", title: "Marco y definiciones", lessons: [
        L("v1-l1", "¿Qué es y qué no es acoso?", 9, 0, false, "presentation"),
        L("v1-l2", "Tipos: psicológico, sexual, discriminatorio", 11, 30, false, "presentation"),
      ], materials: [M("Marco legal acoso laboral.pdf", "420 KB")] },
      { id: "v2", title: "Detección y reporte", lessons: [
        L("v2-l1", "Señales tempranas", 7, 18, false, "presentation"),
        L("v2-l2", "Canal interno de denuncia", 6, 30, false, "presentation"),
        L("v2-l3", "Caso práctico", 10, 0, false, "presentation"),
      ], materials: [M("Protocolo Acme contra el acoso.pdf", "680 KB")] },
      { id: "v3", title: "Evaluación", lessons: [
        L("v3-l1", "Test final (10 preguntas)", 10, 0, false, "presentation"),
      ], materials: [] },
    ],
    forum: [],
  },

  {
    id: "atencion-cliente",
    title: "Atención al cliente nivel avanzado",
    summary: "Manejar quejas, clientes difíciles y construir relaciones a largo plazo.",
    instructor: "Sergio Iglesias",
    instructorRole: "Head of Customer Success",
    category: "Habilidades",
    categoryId: "habilidades",
    type: "video",
    mandatory: false,
    deadline: null,
    deadlineDaysLeft: null,
    durationMin: 168,
    students: 11,
    rating: 4.6,
    enrolled: false,
    accent: "brand",
    description: "Cómo pasar de \"resolver tickets\" a construir relaciones que retienen. Incluye guion para conversaciones difíciles y plantillas reales que usamos en Acme.",
    learn: [
      "Estructurar una conversación de queja en 4 pasos",
      "Identificar el problema real (no el reportado)",
      "Convertir una mala experiencia en lealtad",
      "Cuándo y cómo escalar",
    ],
    requirements: ["6+ meses en un rol de cara al cliente."],
    volumes: [
      { id: "v1", title: "Escucha activa", lessons: [
        L("v1-l1", "Las 3 capas del cliente: dato, emoción, intención", 12, 0, false),
        L("v1-l2", "Reformular sin sonar robot", 9, 30, false),
      ], materials: [M("Plantilla escucha activa.pdf", "180 KB")] },
      { id: "v2", title: "Conversaciones difíciles", lessons: [
        L("v2-l1", "Guion en 4 pasos", 14, 12, false),
        L("v2-l2", "Lenguaje que desactiva", 10, 30, false),
        L("v2-l3", "Cuándo escalar", 8, 18, false),
      ], materials: [M("Frases que NUNCA decir.pdf", "120 KB"), M("Guion 4 pasos.pdf", "240 KB")] },
      { id: "v3", title: "Construir relación", lessons: [
        L("v3-l1", "Follow-up que sorprende", 11, 24, false),
        L("v3-l2", "Convertir queja en testimonio", 13, 0, false),
      ], materials: [] },
    ],
    forum: [],
  },
];

// -----------------------------------------------------------
// Helpers de vista
// -----------------------------------------------------------
export function progressOfCourse(c) {
  const total = totalLessons(c);
  const done = completedLessons(c);
  return total === 0 ? 0 : done / total;
}
export function inProgressCourses() {
  return FORMACION_COURSES.filter((c) => c.enrolled && progressOfCourse(c) > 0 && progressOfCourse(c) < 1);
}
export function mandatoryPending() {
  return FORMACION_COURSES.filter((c) => c.mandatory && progressOfCourse(c) < 1);
}
export function findCourse(id) {
  return FORMACION_COURSES.find((c) => c.id === id);
}
export function totalForumThreads(c) {
  return c.forum.length;
}
export function totalMaterials(c) {
  return c.volumes.reduce((a, v) => a + v.materials.length, 0);
}
export function totalVolumes(c) {
  return c.volumes.length;
}
